import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tablet } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const digits = (v) => v.replace(/\D/g, "").slice(0, 4);

// Profile card: create or change your own Kitchen PIN (identity verified by password or current PIN).
export function KitchenPinCard() {
  const [st, setSt] = useState(null);
  const [f, setF] = useState({ password: "", current_pin: "", new_pin: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/tablet/my-pin").then((r) => setSt(r.data)).catch((e) => setSt({ error: e?.response?.data?.detail || "Kitchen PIN unavailable" }));
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const save = async () => {
    if (f.new_pin.length !== 4 || f.new_pin !== f.confirm) { toast.error("Enter the same 4-digit PIN twice"); return; }
    setBusy(true);
    try {
      await api.post("/tablet/my-pin", { new_pin: f.new_pin, password: f.password || undefined, current_pin: f.current_pin || undefined });
      toast.success(st.has_pin ? "Kitchen PIN changed" : "Kitchen PIN created");
      setF({ password: "", current_pin: "", new_pin: "", confirm: "" }); load();
    } catch (e) { toast.error(e?.normalized?.message || e?.response?.data?.detail || "Could not save PIN"); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: k === "password" ? e.target.value : digits(e.target.value) });
  return (
    <div className="cc-card p-5 mb-5 space-y-3" data-testid="profile-kitchen-pin-section">
      <div className="flex items-center gap-2"><Tablet size={18} className="text-brand" /><h3 className="font-semibold text-slate-800">Kitchen PIN</h3>
        {st.has_pin !== undefined && <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${st.has_pin ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid="kitchen-pin-status">{st.has_pin ? "PIN set" : "No PIN yet"}</span>}</div>
      <p className="text-sm text-slate-500">Your 4-digit PIN identifies you on shared kitchen tablets, so your temperatures, cleaning, deliveries and clock-ins are recorded as you. Never share it. Nobody else can see it.</p>
      {st.error ? <p className="text-sm text-amber-700" data-testid="kitchen-pin-error">{st.error}</p> : st.tablet_access === false ? (
        <p className="text-sm text-amber-700" data-testid="kitchen-pin-access-off">Kitchen tablet access is switched off for you. Ask your manager to turn it on.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {st.password_check_available && <div className="sm:col-span-2"><Label>Your account password{st.has_pin ? " (or use your current PIN)" : ""}</Label>
            <Input type="password" autoComplete="current-password" value={f.password} onChange={set("password")} data-testid="kitchen-pin-password" /></div>}
          {st.has_pin && <div><Label>Current PIN</Label><Input inputMode="numeric" type="password" value={f.current_pin} onChange={set("current_pin")} data-testid="kitchen-pin-current" /></div>}
          <div><Label>New PIN</Label><Input inputMode="numeric" type="password" value={f.new_pin} onChange={set("new_pin")} data-testid="kitchen-pin-new" /></div>
          <div><Label>Confirm new PIN</Label><Input inputMode="numeric" type="password" value={f.confirm} onChange={set("confirm")} data-testid="kitchen-pin-confirm" /></div>
          {!st.password_check_available && !st.has_pin && <p className="sm:col-span-2 text-xs text-slate-500">Your sign-in method has no password to confirm with. Ask a manager for a one-time setup code and set your PIN on the tablet.</p>}
          <div className="sm:col-span-2"><Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="kitchen-pin-save">{st.has_pin ? "Change PIN" : "Create PIN"}</Button>
            <span className="text-xs text-slate-500 ml-3">Forgotten your PIN? Enter your password and choose a new one.</span></div>
        </div>
      )}
    </div>
  );
}
